import { TowerSystem } from './TowerSystem';
import { Wall } from '../types/GameTypes';

export interface RepairTarget {
  id: string;
  type: 'tower' | 'wall';
  hp: number;
  maxHp: number;
}

export class RepairSystem {
  private activeRepairs: Map<string, { startTime: number; duration: number; target: RepairTarget }> = new Map();

  startRepair(target: RepairTarget, buildingCost: number): { success: boolean; cost?: number } {
    // Check if already repairing
    if (this.activeRepairs.has(target.id)) {
      return { success: false };
    }

    // Check if needs repair
    if (target.hp >= target.maxHp) {
      return { success: false };
    }

    // Calculate repair duration: ~1 second per 10% HP missing
    const missingHpPercent = (target.maxHp - target.hp) / target.maxHp;
    const duration = Math.max(0.1, missingHpPercent * 10); // 1s per 10% missing HP

    // Calculate repair cost: +5% of building cost per 10% missing HP, max 45%
    const costPercent = Math.min(0.45, Math.ceil(missingHpPercent * 10) * 0.05);
    const cost = Math.floor(buildingCost * costPercent);

    this.activeRepairs.set(target.id, {
      startTime: Date.now() / 1000,
      duration,
      target: { ...target },
    });

    return { success: true, cost };
  }

  update(_deltaTime: number, towerSystem: TowerSystem, walls: Wall[]): void {
    const currentTime = Date.now() / 1000;
    const completed: string[] = [];

    for (const [id, repair] of this.activeRepairs.entries()) {
      const elapsed = currentTime - repair.startTime;
      
      if (elapsed >= repair.duration) {
        // Repair complete
        if (repair.target.type === 'tower') {
          const tower = towerSystem.getTower(id);
          if (tower) {
            towerSystem.repairTower(tower);
          }
        } else if (repair.target.type === 'wall') {
          const wall = walls.find(w => w.id === id);
          if (wall) {
            wall.hp = wall.maxHp;
          }
        }
        completed.push(id);
      }
    }

    // Remove completed repairs
    for (const id of completed) {
      this.activeRepairs.delete(id);
    }
  }

  cancelRepair(id: string): void {
    this.activeRepairs.delete(id);
  }

  isRepairing(id: string): boolean {
    return this.activeRepairs.has(id);
  }

  getRepairProgress(id: string): number {
    const repair = this.activeRepairs.get(id);
    if (!repair) return 0;

    const currentTime = Date.now() / 1000;
    const elapsed = currentTime - repair.startTime;
    return Math.min(1, elapsed / repair.duration);
  }

  clearAll(): void {
    this.activeRepairs.clear();
  }
}
